import { useForm } from 'react-hook-form'

import { useMyMonitoringUpdateMyMonitoringUser } from '@apps-common/apis/my-monitoring/users'
import { useUpdateClientAccountAssociation } from '@apps-common/apis/portal/client-accounts'
import { ButtonRoundedSm } from '@apps-common/components/elements/button'
import { CheckboxInputSM, InputInfoSM, InputSM } from '@apps-common/components/elements/form'
import { ModalBody, ModalFooter } from '@apps-common/components/modals/modal'
import { MESSAGE_IS_MM_ADMIN } from '@apps-common/config/app-messages'
import { commonIcons } from '@apps-common/config/icons'
import { ModalDismiss } from '@apps-common/config/modal'
import { useProjectHook } from '@apps-common/hooks/useProjectHook'
import { IMyMonitoringUserModel } from '@apps-common/types/portal/users'

interface IProps {
  uId: string
  user: IMyMonitoringUserModel
  closeCb: (role: string) => void
  resetCb: () => void
}

export const MyMonitoringUserAddExistingForm: React.FC<IProps> = ({ uId, user, closeCb, resetCb }) => {
  const useUpdateUserHook = useProjectHook(useUpdateClientAccountAssociation, useMyMonitoringUpdateMyMonitoringUser)

  const { mutate: updateUser, isPending: updateUserIsPending } = useUpdateUserHook(true, () => closeCb(ModalDismiss.Confirm))

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors }
  } = useForm<IMyMonitoringUserModel>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      id: user.id,
      unitId: uId
    }
  })

  const submit = (values: IMyMonitoringUserModel) => {
    if (values.userPartition && values.userPartition.length > 0) {
      values.mapPartition = true
    }

    updateUser({ model: values })
  }

  const loading = isSubmitting || updateUserIsPending

  return (
    <>
      <ModalBody classes='!pt-[95px]'>
        <div className='w-full flex items-center justify-end'>
          <ButtonRoundedSm
            type='button'
            loading={loading}
            size='xs'
            text='Change email'
            icon={<commonIcons.arrowAngled />}
            iconRight
            className='action-brand grow-0'
            callback={resetCb}
          />
        </div>
        <form noValidate id='my-monitoring-user-add-existing-form' onSubmit={handleSubmit(submit)}>
          <InputInfoSM id='user-email' label='Email' showLabel value={user.email} />
          <InputInfoSM id='user-name' label='Name' showLabel value={`${user.firstName} ${user.lastName}`} />
          <InputSM
            key='userNumber'
            type='text'
            placeholder='1 to 999'
            label='User number (Zone)'
            id='userNumber'
            showLabel
            disabled={loading}
            registration={register('userNumber', {
              required: true,
              pattern: {
                value: /^[0-9]{3}$/,
                message: '3 numerical characters only e.g. 001'
              }
            })}
            errors={errors.userNumber}
          />
          <InputSM
            key='userPartition'
            type='text'
            placeholder='0 to 99'
            label='User partition (If required)'
            id='userPartition'
            showLabel
            disabled={loading}
            registration={register('userPartition', {
              required: false,
              pattern: {
                value: /^[0-9]{2}$/,
                message: '2 numerical characters only e.g. 01'
              }
            })}
            errors={errors.userPartition}
          />
          <CheckboxInputSM
            label='Site administrator'
            id='isAdmin'
            loading={false}
            disabled={loading}
            registration={register('isAdmin')}
            errors={errors.isAdmin}
            info={MESSAGE_IS_MM_ADMIN}
          />
        </form>
      </ModalBody>
      <ModalFooter>
        <div className='flex items-center justify-between gap-2'>
          <ButtonRoundedSm
            type='submit'
            form='my-monitoring-user-add-existing-form'
            className='action-success'
            loading={loading}
            text='Create'
          />
          <ButtonRoundedSm
            type='button'
            className='action-neutral'
            loading={loading}
            text='Cancel'
            callback={() => closeCb(ModalDismiss.Cancel)}
          />
        </div>
      </ModalFooter>
    </>
  )
}
